import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { HomeContentContainer } from '@/components'
import { Loader } from '@/components/common/Loader'
import { PicklistManagementList } from '@/components/pages/Admin/PicklistManagement/PicklistManagementList'
import { useTranslations } from '@/hooks/useTranslations'
import { useProfileData } from '@/hooks/useProfile'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { PicklistKind } from '@/modules/picklist/services/picklist'

function normalizeTab(raw: string | undefined): PicklistKind {
  if (raw === 'grade' || raw === 'durian_type' || raw === 'strap_band' || raw === 'expense_type') return raw
  return 'durian_type'
}

export const Route = createFileRoute('/(dashboard)/admin/picklist')({
  component: PicklistPage,
  validateSearch: (search: Record<string, unknown>) => {
    const tab = search.tab as string | undefined
    return { tab: tab || 'durian_type' }
  },
})

function PicklistPage() {
  const t = useTranslations('admin.picklist_management')
  const navigate = useNavigate()
  const { tab: tabSearch } = Route.useSearch()
  const { checkPermission, isLoading: profileLoading } = useProfileData()

  const canPicklist = checkPermission('admin_picklist')
  const tab = normalizeTab(tabSearch)

  useEffect(() => {
    if (tabSearch !== tab) {
      navigate({ to: '/admin/picklist', search: { tab }, replace: true })
    }
  }, [navigate, tab, tabSearch])

  const setTab = (value: string) => {
    navigate({ to: '/admin/picklist', search: { tab: normalizeTab(value) }, replace: true })
  }

  if (profileLoading) {
    return (
      <HomeContentContainer title={t('title')}>
        <Loader.FullScreen />
      </HomeContentContainer>
    )
  }

  if (!canPicklist) {
    return (
      <HomeContentContainer title={t('title')}>
        <p className="text-muted-foreground">{t('no_access')}</p>
      </HomeContentContainer>
    )
  }

  return (
    <HomeContentContainer title={t('title')}>
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-2 grid w-full max-w-4xl grid-cols-4">
          <TabsTrigger value="durian_type" className="text-base">
            {t('tab_durian')}
          </TabsTrigger>
          <TabsTrigger value="grade" className="text-base">
            {t('tab_grade')}
          </TabsTrigger>
          <TabsTrigger value="strap_band" className="text-base">
            {t('tab_strap')}
          </TabsTrigger>
          <TabsTrigger value="expense_type" className="text-base">
            {t('tab_expense')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="durian_type" className="mt-0 outline-none">
          <PicklistManagementList kind="durian_type" />
        </TabsContent>
        <TabsContent value="grade" className="mt-0 outline-none">
          <PicklistManagementList kind="grade" />
        </TabsContent>
        <TabsContent value="strap_band" className="mt-0 outline-none">
          <PicklistManagementList kind="strap_band" />
        </TabsContent>
        <TabsContent value="expense_type" className="mt-0 outline-none">
          <PicklistManagementList kind="expense_type" />
        </TabsContent>
      </Tabs>
    </HomeContentContainer>
  )
}
